import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import customFetch from '../../api/customFetch.js';
import mammoth from 'mammoth';

export default function RegulationsPage() {
  const [regulations, setRegulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [marking, setMarking] = useState(false);
  const [docxHtml, setDocxHtml] = useState(null);
  const [docxLoading, setDocxLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    loadRegulations();
  }, []);

  const loadRegulations = async () => {
    try {
      setLoading(true);
      const { data } = await customFetch.get('/regulations/my');
      setRegulations(data.regulations || []);
    } catch {
      setError('שגיאה בטעינת התקנונים');
    } finally {
      setLoading(false);
    }
  };

  const openRegulation = async (reg) => {
    setDocxHtml(null);
    try {
      const { data } = await customFetch.get(`/regulations/${reg._id}/content`);
      setSelected(data.regulation);

      // pre-load Word files immediately so the viewer is ready
      if (data.regulation.fileType === 'word' && data.regulation.fileUrl) {
        loadDocx(data.regulation.fileUrl);
      }
    } catch {
      setError('שגיאה בטעינת תוכן התקנון');
    }
  };

  const loadDocx = useCallback(async (fileUrl) => {
    setDocxLoading(true);
    try {
      const res = await fetch(fileUrl);
      const buf = await res.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer: buf });
      setDocxHtml(result.value);
    } catch {
      setDocxHtml('<p style="color:#888;text-align:center">לא ניתן להציג את המסמך – ניתן להורידו ולפתוח בוורד</p>');
    } finally {
      setDocxLoading(false);
    }
  }, []);

  const markRead = async () => {
    if (!selected) return;
    try {
      setMarking(true);
      await customFetch.post(`/regulations/${selected._id}/read`);
      setRegulations((prev) =>
        prev.map((r) =>
          r._id === selected._id ? { ...r, hasRead: true, readAt: new Date() } : r
        )
      );
      setSelected(null);
      setDocxHtml(null);
    } catch {
      setError('שגיאה בסימון הקריאה');
    } finally {
      setMarking(false);
    }
  };

  const unreadCount = regulations.filter((r) => !r.hasRead).length;

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingCenter}>
          <div style={styles.spinner}></div>
          <span>טוען תקנונים...</span>
        </div>
      </div>
    );
  }

  // Modal - תוכן תקנון
  if (selected) {
    return (
      <div style={styles.page}>
        <div style={styles.modalOverlay} onClick={() => { setSelected(null); setDocxHtml(null); }}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>📋 {selected.title}</h2>
              <button style={styles.closeBtn} onClick={() => { setSelected(null); setDocxHtml(null); }}>✕</button>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              {selected.description && (
                <p style={styles.description}>{selected.description}</p>
              )}

              {/* Embedded File Viewer */}
              {selected.fileUrl && (() => {
                const absoluteUrl = selected.fileUrl.startsWith('http')
                  ? selected.fileUrl
                  : `${window.location.origin}${selected.fileUrl}`;

                if (selected.fileType === 'pdf') {
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div style={styles.fileBar}>
                        <span>{selected.fileType === 'pdf' ? '📕' : '📘'} {selected.fileName}</span>
                        <a href={absoluteUrl} download style={styles.downloadLink}>⬇️ הורד</a>
                      </div>
                      <iframe
                        src={absoluteUrl}
                        title={selected.fileName}
                        style={styles.fileViewer}
                      />
                    </div>
                  );
                }

                // Word - mammoth in-browser conversion
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div style={styles.fileBar}>
                      <span>📘 {selected.fileName}</span>
                      <a href={absoluteUrl} download style={styles.downloadLink}>⬇️ הורד</a>
                    </div>
                    <div style={styles.docxViewer}>
                      {docxLoading ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                          <div style={styles.spinner}></div>
                          <p>טוען מסמך...</p>
                        </div>
                      ) : docxHtml ? (
                        <div
                          style={styles.docxContent}
                          dangerouslySetInnerHTML={{ __html: docxHtml }}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              })()}

              {/* Text Content */}
              {selected.textContent && (
                <div style={styles.textContent}>
                  {selected.textContent.split('\n').map((line, i) =>
                    line.trim() ? (
                      <p key={i} style={{ margin: '0 0 12px' }}>{line}</p>
                    ) : (
                      <br key={i} />
                    )
                  )}
                </div>
              )}

              {!selected.textContent && !selected.fileUrl && (
                <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>
                  אין תוכן מוצג לתקנון זה
                </p>
              )}
            </div>

            {/* Modal Footer */}
            <div style={styles.modalFooter}>
              {regulations.find((r) => r._id === selected._id)?.hasRead ? (
                <div style={styles.alreadyRead}>
                  ✅ סימנת שקראת תקנון זה
                </div>
              ) : (
                <button
                  style={styles.readBtn}
                  onClick={markRead}
                  disabled={marking}
                >
                  {marking ? '⏳ מסמן...' : '✅ קראתי את התקנון'}
                </button>
              )}
              <button style={styles.closeFooterBtn} onClick={() => { setSelected(null); setDocxHtml(null); }}>
                סגור
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => nav('/user/dashboard')}>
          ← חזור לדשבורד
        </button>
        <h1 style={styles.title}>📋 תקנונים</h1>
        {unreadCount > 0 && (
          <div style={styles.unreadBadge}>
            {unreadCount} תקנונים לא נקראו
          </div>
        )}
      </div>

      {error && (
        <div style={styles.errorBox}>
          ⚠️ {error}
          <button onClick={() => setError(null)} style={styles.closeError}>✕</button>
        </div>
      )}

      {regulations.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>📋</div>
          <h3>אין תקנונים הרלוונטיים עבורך</h3>
        </div>
      ) : (
        <>
          {/* Progress Summary */}
          <div style={styles.summaryCard}>
            <div style={styles.summaryItem}>
              <span style={{ ...styles.summaryNum, color: '#22c55e' }}>
                {regulations.filter((r) => r.hasRead).length}
              </span>
              <span style={styles.summaryLabel}>נקראו</span>
            </div>
            <div style={styles.summaryDivider} />
            <div style={styles.summaryItem}>
              <span style={{ ...styles.summaryNum, color: '#ef4444' }}>{unreadCount}</span>
              <span style={styles.summaryLabel}>לא נקראו</span>
            </div>
            <div style={styles.summaryDivider} />
            <div style={styles.summaryItem}>
              <span style={{ ...styles.summaryNum, color: '#3b82f6' }}>{regulations.length}</span>
              <span style={styles.summaryLabel}>סה"כ</span>
            </div>
          </div>

          {/* Regulation Cards */}
          <div style={styles.grid}>
            {regulations.map((reg) => (
              <div
                key={reg._id}
                style={{
                  ...styles.regCard,
                  borderColor: reg.hasRead ? '#22c55e' : '#ef4444',
                  opacity: reg.hasRead ? 0.85 : 1,
                }}
                onClick={() => openRegulation(reg)}
              >
                <div style={styles.regCardIcon}>
                  {reg.hasRead ? '✅' : '📋'}
                </div>
                <div style={styles.regCardContent}>
                  <h3 style={styles.regTitle}>{reg.title}</h3>
                  {reg.description && (
                    <p style={styles.regDesc}>{reg.description}</p>
                  )}
                  <div style={styles.regMeta}>
                    {reg.fileUrl && <span>📎 קובץ מצורף</span>}
                    {reg.hasTextContent && <span>📄 תוכן טקסטואלי</span>}
                    <span>{new Date(reg.createdAt).toLocaleDateString('he-IL')}</span>
                  </div>
                </div>
                <div style={styles.regStatus}>
                  {reg.hasRead ? (
                    <span style={styles.readStatus}>
                      ✓ נקרא<br />
                      <small>{new Date(reg.readAt).toLocaleDateString('he-IL')}</small>
                    </span>
                  ) : (
                    <span style={styles.unreadStatus}>לקריאה ←</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0cc0df 0%, #077b94 50%, #034159 100%)',
    padding: '24px 16px',
    direction: 'rtl',
  },
  loadingCenter: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '60vh', gap: 16, color: '#fff',
  },
  spinner: {
    width: 40, height: 40, borderRadius: '50%',
    border: '3px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff', animation: 'spin 0.8s linear infinite',
  },
  header: {
    maxWidth: 900, margin: '0 auto 24px', display: 'flex',
    alignItems: 'center', gap: 16, flexWrap: 'wrap',
  },
  backBtn: {
    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
    color: '#fff', padding: '8px 16px', borderRadius: 10, cursor: 'pointer',
    fontSize: '0.9rem', fontWeight: 600,
  },
  title: { color: '#fff', margin: 0, fontSize: '1.6rem', fontWeight: 700 },
  unreadBadge: {
    background: '#ef4444', color: '#fff', padding: '4px 12px',
    borderRadius: 20, fontSize: '0.85rem', fontWeight: 600,
  },
  errorBox: {
    maxWidth: 900, margin: '0 auto 16px', background: '#fee2e2', color: '#991b1b',
    padding: '12px 16px', borderRadius: 10, display: 'flex', gap: 10, alignItems: 'center',
  },
  closeError: { marginRight: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 },
  emptyState: {
    maxWidth: 900, margin: '0 auto', background: '#fff', borderRadius: 16,
    padding: 60, textAlign: 'center', color: '#555',
  },
  summaryCard: {
    maxWidth: 900, margin: '0 auto 24px', background: '#fff', borderRadius: 16,
    padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 32px' },
  summaryNum: { fontSize: '2rem', fontWeight: 700 },
  summaryLabel: { fontSize: '0.85rem', color: '#6b7280', marginTop: 4 },
  summaryDivider: { width: 1, height: 50, background: '#e5e7eb' },
  grid: { maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 },
  regCard: {
    background: '#fff', borderRadius: 14, padding: '16px 20px',
    display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)', border: '2px solid',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  regCardIcon: { fontSize: '2rem', flexShrink: 0 },
  regCardContent: { flex: 1 },
  regTitle: { margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#111' },
  regDesc: { margin: '0 0 8px', fontSize: '0.85rem', color: '#6b7280' },
  regMeta: { display: 'flex', gap: 12, fontSize: '0.8rem', color: '#9ca3af', flexWrap: 'wrap' },
  regStatus: { flexShrink: 0, textAlign: 'center' },
  readStatus: { fontSize: '0.8rem', color: '#22c55e', fontWeight: 600, lineHeight: 1.4 },
  unreadStatus: { background: '#3b82f6', color: '#fff', padding: '6px 12px', borderRadius: 20, fontSize: '0.85rem', fontWeight: 600 },

  // Modal
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 900,
    maxHeight: '95vh', display: 'flex', flexDirection: 'column', direction: 'rtl',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  modalHeader: {
    padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  modalTitle: { margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#111' },
  closeBtn: {
    background: '#f3f4f6', border: 'none', borderRadius: 8, width: 32, height: 32,
    cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modalBody: { flex: 1, overflowY: 'auto', padding: '20px 24px' },
  description: { color: '#6b7280', marginBottom: 16, fontSize: '0.95rem' },
  fileCard: {
    display: 'flex', alignItems: 'center', gap: 16, padding: '14px 16px',
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 20,
  },
  fileName: { fontWeight: 600, color: '#111', fontSize: '0.95rem' },
  fileType: { fontSize: '0.8rem', color: '#6b7280', marginTop: 2 },
  openBtn: {
    background: '#3b82f6', color: '#fff', padding: '8px 16px', borderRadius: 8,
    textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap',
  },
  fileBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 12px', background: '#f1f5f9', borderRadius: '8px 8px 0 0',
    fontSize: '0.85rem', color: '#374151', fontWeight: 600,
    border: '1px solid #e2e8f0', borderBottom: 'none',
  },
  downloadLink: {
    color: '#3b82f6', textDecoration: 'none', fontSize: '0.8rem', fontWeight: 600,
  },
  fileViewer: {
    width: '100%', height: 520, border: '1px solid #e2e8f0',
    borderRadius: '0 0 8px 8px', display: 'block',
  },
  docxViewer: {
    border: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px',
    minHeight: 200, background: '#fff',
  },
  docxContent: {
    padding: '24px 32px', lineHeight: 1.8, color: '#1f2937',
    fontSize: '0.95rem', direction: 'rtl', textAlign: 'right',
  },
  textContent: {
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
    padding: '20px 24px', lineHeight: 1.8, color: '#1f2937',
    fontSize: '0.95rem', whiteSpace: 'pre-wrap',
  },
  modalFooter: {
    padding: '16px 24px', borderTop: '1px solid #e5e7eb',
    display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end',
  },
  alreadyRead: { color: '#22c55e', fontWeight: 600, fontSize: '0.95rem', marginLeft: 'auto' },
  readBtn: {
    background: '#22c55e', color: '#fff', border: 'none', borderRadius: 10,
    padding: '10px 24px', cursor: 'pointer', fontWeight: 700, fontSize: '1rem',
    transition: 'background 0.2s',
  },
  closeFooterBtn: {
    background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 10,
    padding: '10px 20px', cursor: 'pointer', fontWeight: 600,
  },
};
